import { useEffect } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Link,
  Chip,
  Button,
} from "@mui/material";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import useGetConsent from "../../hooks/useGetConsents";
import { formatDate } from "../../utils/helpers";

const getStatusChip = (status) => {
  switch (status) {
    case "completed":
      return <Chip label="Signed" color="success" size="small" />;
    case "declined":
      return <Chip label="Declined" color="error" size="small" />;
    default:
      return <Chip label="Pending" color="warning" size="small" />;
  }
};

const EConsent = () => {
  const { _id_: patientId } = useParams();
  const navigate = useNavigate();

  const { trialFilter, setActive, setBreadcrumbTitle } = useOutletContext();

  useEffect(() => {
    setActive?.("econsent");
    setBreadcrumbTitle?.("EConsent");
  }, [setActive, setBreadcrumbTitle]);

  const { data: consentData = [] } = useGetConsent({ patientId });

  const selectedConsent = consentData.find((c) => String(c.trial_id) === trialFilter);

  const getSigningUrl = (sub, row) => {
    // New API format: signing_url directly on submitter
    if (sub.signing_url) return sub.signing_url;
    // Old API format: current_user_signing as separate field, match by role
    if (
      row.current_user_signing &&
      row.current_user_signing.role === sub.role &&
      sub.status !== "completed" &&
      sub.status !== "declined"
    ) {
      return row.current_user_signing.embed_src;
    }
    return null;
  };

  const headerCellSx = {
    backgroundColor: (theme) => theme.palette.grey[100],
    fontWeight: "bold",
    textTransform: "uppercase",
    fontSize: "0.85rem",
    color: "#424242",
    whiteSpace: "nowrap",
  };

  const bodyCellSx = {
    whiteSpace: "nowrap",
  };

  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      {/* Header */}
      <Stack spacing={2} mb={2}>
        <Typography variant="h6">EConsent</Typography>
      </Stack>

      {/* Empty state */}
      {consentData.length === 0 && (
        <Typography color="text.secondary" align="center" sx={{ py: 6 }}>
          No consents found
        </Typography>
      )}

      {consentData.length > 0 && !selectedConsent && (
        <Typography color="text.secondary" align="center" sx={{ py: 6 }}>
          No consent found for the selected trial
        </Typography>
      )}

      {/* Consent info + submitters table */}
      {selectedConsent && (
        <Stack spacing={2}>
          {/* Consent summary */}
          <Box
            sx={{
              display: "flex",
              gap: 3,
              flexWrap: "wrap",
              alignItems: "flex-end",
            }}
          >
            <Typography variant="body2">
              <strong>Type:</strong> {selectedConsent.consent ? "eConsent" : "Digital Consent"}
            </Typography>
            {selectedConsent.digital_consent && (
              <Link
                component="button"
                underline="hover"
                variant="body1"
                fontWeight={"fontWeightBold"}
                onClick={() => window.open(selectedConsent.digital_consent, "_blank")}
              >
                View Document
              </Link>
            )}
            {selectedConsent.consent && (
              <Link
                component="button"
                underline="hover"
                variant="body1"
                fontWeight={"fontWeightBold"}
                onClick={() =>
                  navigate(`/patient/${patientId}/e_consent/${selectedConsent.trial_id}`)
                }
              >
                View Consent
              </Link>
            )}
          </Box>

          {/* Submitters / signing parties table */}
          {selectedConsent.submitters && selectedConsent.submitters.length > 0 && (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: "60vh" }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Role</TableCell>
                    <TableCell sx={headerCellSx}>Email</TableCell>
                    <TableCell sx={headerCellSx}>Status</TableCell>
                    <TableCell sx={headerCellSx}>Signed Date</TableCell>
                    <TableCell sx={headerCellSx}>Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {selectedConsent.submitters.map((sub) => {
                    const signingUrl = getSigningUrl(sub, selectedConsent);
                    return (
                      <TableRow key={sub.email + sub.role}>
                        <TableCell sx={bodyCellSx}>{sub.role}</TableCell>
                        <TableCell sx={bodyCellSx}>{sub.email}</TableCell>
                        <TableCell sx={bodyCellSx}>{getStatusChip(sub.status)}</TableCell>
                        <TableCell sx={bodyCellSx}>{formatDate(sub.completed_at)}</TableCell>
                        <TableCell sx={bodyCellSx}>
                          {signingUrl ? (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => window.open(signingUrl, "_blank")}
                            >
                              Sign
                            </Button>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      )}
    </Paper>
  );
};

export default EConsent;
